/** @format */

/**
 * Core API validation for the CSV import (QB2-223).
 *
 * Validates an import's paper-level selection and each row's specification
 * against the Core Taxonomy API. Every check is advisory: it returns WARNINGS,
 * never hard failures (DR3). The import still proceeds; the dry-run surfaces the
 * warnings so a human decides. Core being unavailable or incomplete must never
 * block an import.
 */

import type { Brand } from "@question-bank/shared";
import {
	CoreTaxonomyError,
	listEditions,
	listSubjects,
	resolveSubtopic,
	type ResolveResponse,
	type SpecEdition,
} from "../../api/coreTaxonomy";

/** The paper-level selection chosen on the import page (DR8). */
export interface PaperLevelSelection {
	brand: Brand;
	subject: string;
	/**
	 * Core qualification `code` (e.g. `alevel`) chosen from the Core dropdown
	 * (QB2-223). Passed straight into the Core specification path.
	 */
	qualification: string;
	/** Core tier `code` chosen on the import page; `undefined` when no tier. */
	tier?: string;
	/** Core series (season) `code` chosen on the import page. */
	season: string;
	year: number;
}

/** The question-level specification fields a row contributes to Core validation. */
export interface RowSpecification {
	theme?: string;
	topic?: string;
	subTopic?: string;
}

export type WarningKind =
	| "subject-unknown"
	| "edition-none"
	| "spec-unresolved"
	| "spec-ambiguous"
	| "core-unavailable";

export interface CoreWarning {
	kind: WarningKind;
	message: string;
}

/** Result of validating the paper-level selection against Core reference data. */
export interface PaperValidationResult {
	warnings: CoreWarning[];
	/**
	 * The `current` edition resolved for (subject, qualification), used to
	 * resolve each row's subtopic. `undefined` when none could be resolved
	 * (a warning is recorded in that case).
	 */
	currentEdition?: SpecEdition;
}

/** Per-row Core validation outcome. */
export interface RowValidationResult {
	warnings: CoreWarning[];
	/** The single resolved subtopic candidate, when exactly one matched. */
	resolved?: ResolveResponse["candidates"][number];
}

const titleOf = (item: { title: { en: string } }): string => item.title.en;

/**
 * Choose the authoritative "latest" edition: the one whose status is `current`
 * (DR2). The last element of the list is NOT used — it may be a `draft`, which
 * `resolveSubtopic` refuses with a 409.
 */
export const pickCurrentEdition = (editions: SpecEdition[]): SpecEdition | undefined =>
	editions.find((edition) => edition.status === "current");

/**
 * Validate the paper-level selection against Core reference data. Confirms the
 * chosen subject exists, and resolves the `current` edition for (subject,
 * qualification) so rows can be resolved against it. Tier and series are now
 * chosen from Core-sourced dropdowns on the import page, so a value absent from
 * Core cannot be picked and no existence check is needed for them (QB2-223).
 * All findings are warnings. A Core outage yields a single `core-unavailable`
 * warning and an undefined edition — the import still proceeds.
 */
export const validatePaperSelection = async (selection: PaperLevelSelection): Promise<PaperValidationResult> => {
	const warnings: CoreWarning[] = [];
	const { brand, subject, qualification } = selection;

	try {
		const subjects = await listSubjects(brand);

		if (!subjects.data.some((candidate) => titleOf(candidate).toLowerCase() === subject.toLowerCase())) {
			warnings.push({ kind: "subject-unknown", message: `Subject "${subject}" was not found in the Core API.` });
		}
	} catch (error) {
		return { warnings: [coreUnavailableWarning(error)] };
	}

	// Resolve the current edition for (subject, qualification) so rows can be
	// resolved against a concrete version. `qualification` is the Core code.
	try {
		const editions = await listEditions(brand, subject, qualification);
		const currentEdition = pickCurrentEdition(editions.data);
		if (!currentEdition) {
			warnings.push({
				kind: "edition-none",
				message: `No current specification edition was found for ${subject} (${qualification}). Row specifications cannot be checked.`,
			});
			return { warnings };
		}
		return { warnings, currentEdition };
	} catch (error) {
		warnings.push(coreUnavailableWarning(error));
		return { warnings };
	}
};

/**
 * Validate a single row's specification against the resolved edition. Resolves
 * theme/topic/subtopic to a Core subtopic:
 *  - exactly one candidate  → matched (returned for pinning)
 *  - zero candidates        → `spec-unresolved` warning
 *  - two or more candidates → `spec-ambiguous` warning (needs a human)
 * A row without a full theme/topic/subtopic triple is skipped (no warning).
 */
export const validateRowSpecification = async (
	selection: PaperLevelSelection,
	edition: SpecEdition,
	row: RowSpecification,
): Promise<RowValidationResult> => {
	const warnings: CoreWarning[] = [];

	if (!row.theme || !row.topic || !row.subTopic) {
		return { warnings };
	}

	try {
		const response = await resolveSubtopic(selection.brand, selection.subject, selection.qualification, {
			version: edition.label,
			theme: row.theme,
			topic: row.topic,
			subtopic: row.subTopic,
		});
		const candidates = response.candidates;
		if (candidates.length === 1) {
			return { warnings, resolved: candidates[0] };
		}
		if (candidates.length === 0) {
			warnings.push({
				kind: "spec-unresolved",
				message: `No Core subtopic matched "${row.theme} / ${row.topic} / ${row.subTopic}".`,
			});
			return { warnings };
		}
		warnings.push({
			kind: "spec-ambiguous",
			message: `"${row.theme} / ${row.topic} / ${row.subTopic}" matched ${candidates.length} Core subtopics; a human must choose.`,
		});
		return { warnings };
	} catch (error) {
		warnings.push(coreUnavailableWarning(error));
		return { warnings };
	}
};

/** Build the single advisory warning used whenever a Core read fails. */
const coreUnavailableWarning = (error: unknown): CoreWarning => {
	const detail =
		error instanceof CoreTaxonomyError
			? ` (status ${error.status})`
			: error instanceof Error && error.message
				? ` (${error.message})`
				: "";
	return {
		kind: "core-unavailable",
		message: `The Core API could not be reached${detail}; validation was skipped for this import.`,
	};
};
